import { getCommunityConfig } from "@framework/config/community/community.config";
import automaticPersistedQueryMiddleware from "@framework/relay/server/network/middlewares/automatic-persisted-query-middleware";
import basicAuthMiddleware from "@framework/relay/server/network/middlewares/basic-auth-middleware";
import hydrationMiddleware from "@framework/relay/server/network/middlewares/hydration-middleware";
import logMiddleware from "@framework/relay/server/network/middlewares/log-middleware";
import ServerMiddleware from "@framework/relay/server/network/server-middleware";
import { HydrationData } from "@framework/relay/shared/hydration";
import bypassCacheMiddleware from "@framework/relay/shared/middlewares/bypassCacheMiddleware";
import clientHeadersMiddleware from "@framework/relay/shared/middlewares/clientHeadersMiddleware";
import errorMiddleware from "@framework/relay/shared/middlewares/errorMiddleware";
import fastlyDebugHeaderMiddleware from "@framework/relay/shared/middlewares/fastlyDebugHeaderMiddleware";
import logContextMiddleware from "@framework/relay/shared/middlewares/log-context-middleware";
import userAgentMiddleware from "@framework/relay/shared/middlewares/userAgentMiddleware";

interface IsomorphicServerMiddlewareListParams {
  hydrationData: HydrationData | null;
  persisted: Record<string, string>;
  authToken?: string;
  userAgent?: string;
  bypassCache?: boolean;
  fastlyDebugHeader?: boolean;
}

/**
 * Creates a middleware list that is safe to import on both server and client.
 * This excludes server-only middleware like Redis-dependent components.
 * Used by createIsomorphicQueryFetcher which is imported in _app and as a
 * result cannot contain server-only code despite the query fetching only
 * being executed on the server.
 */
export default function createIsomorphicServerMiddlewareList({
  hydrationData,
  persisted,
  userAgent,
  bypassCache,
  fastlyDebugHeader,
}: IsomorphicServerMiddlewareListParams): ServerMiddleware[] {
  const { APP_NAME, VERSION } = process.env;

  /**
   * NOTE: QueryLockMiddleware is intentionally excluded here
   * as it requires Redis and is not safe for client-side execution
   */
  return [
    // Hydration Middleware.
    ...(hydrationData !== null ? [hydrationMiddleware(hydrationData)] : []),
    // Log Context Middleware - builds structured logging context early.
    logContextMiddleware({
      /**
       * Server requests have access to getCommunityConfig which is not available
       * on the client so the middleware accepts a community data resolver function.
       */
      communityDataResolver: (communityId) => {
        const config = getCommunityConfig(communityId);
        return {
          communityId,
          communityName: config.communityName,
          communityApiOrigin: config.communityApiOrigin,
          communityOrigin: config.communityOrigin,
          communityWpSiteId: config.communityWpSiteId,
        };
      },
    }),
    // Log Middleware - can now use ctx.logContext for structured logging.
    logMiddleware,
    // Error Middleware.
    errorMiddleware,
    // Auth Middleware.
    basicAuthMiddleware,
    // Client Headers Middleware.
    clientHeadersMiddleware({
      name: APP_NAME,
      commit: VERSION,
    }),
    // duet's own User-Agent, with the caller's forwarded in a separate header.
    userAgentMiddleware({
      clientUserAgent: userAgent,
    }),
    // Bypass cache header.
    bypassCacheMiddleware({
      bypassCache,
    }),
    // Add fastly-debug header.
    fastlyDebugHeaderMiddleware({
      active: fastlyDebugHeader,
    }),
    // APQ Middleware.
    automaticPersistedQueryMiddleware(persisted),
  ];
}
